import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Brain, 
  Cloud,
  Bot,
  Leaf, 
  Zap,
  Database,
  LineChart,
  Heart,
  Shield,
  Code,
  Network,
  Cpu
} from 'lucide-react';

const jobCategories = [
  {
    name: 'IT',
    jobs: [
      {
        title: 'Specjalista AI/ML',
        icon: Brain,
        description: 'Twórz i wdrażaj rozwiązania sztucznej inteligencji',
        growth: '+45% rocznie',
        salary: '12 000 - 25 000 PLN'
      },
      {
        title: 'Architekt Chmury',
        icon: Cloud,
        description: 'Projektuj i zarządzaj infrastrukturą chmurową',
        growth: '+40% rocznie',
        salary: '15 000 - 28 000 PLN'
      },
      {
        title: 'Inżynier Automatyzacji',
        icon: Bot,
        description: 'Automatyzuj procesy produkcyjne i biznesowe',
        growth: '+35% rocznie',
        salary: '10 000 - 20 000 PLN'
      }
    ]
  },
  {
    name: 'Ekologia',
    jobs: [
      {
        title: 'Manager Zrównoważonego Rozwoju',
        icon: Leaf,
        description: 'Wdrażaj strategie ekologiczne w firmach',
        growth: '+38% rocznie',
        salary: '8 000 - 18 000 PLN'
      },
      {
        title: 'Specjalista OZE',
        icon: Zap,
        description: 'Projektuj i nadzoruj instalacje odnawialnych źródeł energii',
        growth: '+42% rocznie',
        salary: '7 000 - 16 000 PLN'
      }
    ]
  },
  {
    name: 'Analityka',
    jobs: [
      {
        title: 'Data Scientist',
        icon: Database,
        description: 'Analizuj dane i twórz modele predykcyjne',
        growth: '+40% rocznie',
        salary: '12 000 - 22 000 PLN'
      },
      {
        title: 'Analityk Biznesowy',
        icon: LineChart,
        description: 'Optymalizuj procesy biznesowe w oparciu o dane',
        growth: '+35% rocznie',
        salary: '9 000 - 18 000 PLN'
      }
    ]
  },
  {
    name: 'Medycyna',
    jobs: [
      {
        title: 'Specjalista E-Zdrowia',
        icon: Heart,
        description: 'Rozwijaj systemy telemedycyny i e-zdrowia',
        growth: '+48% rocznie',
        salary: '8 000 - 17 000 PLN'
      },
      {
        title: 'Inżynier Biomedyczny',
        icon: Shield,
        description: 'Twórz innowacyjne rozwiązania medyczne',
        growth: '+40% rocznie',
        salary: '9 000 - 19 000 PLN'
      }
    ]
  },
  {
    name: 'Cyberbezpieczeństwo',
    jobs: [
      {
        title: 'Analityk Bezpieczeństwa',
        icon: Code,
        description: 'Chroń firmy przed cyberzagrożeniami',
        growth: '+50% rocznie',
        salary: '10 000 - 23 000 PLN'
      },
      {
        title: 'Etyczny Haker',
        icon: Network,
        description: 'Testuj i wzmacniaj systemy zabezpieczeń',
        growth: '+45% rocznie',
        salary: '12 000 - 25 000 PLN'
      }
    ]
  }
];

export default function FutureJobs() {
  const [selectedCategory, setSelectedCategory] = useState('IT');

  return (
    <div className="bg-gradient-to-b from-transparent to-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">
          Zawody Przyszłości
        </h2>
        <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
          Poznaj najbardziej perspektywiczne zawody następnej dekady w Polsce
        </p>

        {/* Category Tabs */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {jobCategories.map((category) => (
            <button
              key={category.name}
              onClick={() => setSelectedCategory(category.name)}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                selectedCategory === category.name
                  ? 'bg-purple-600 text-white'
                  : 'bg-white text-gray-600 hover:bg-purple-50'
              }`}
            >
              {category.name}
            </button>
          ))}
        </div>

        {/* Jobs Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {jobCategories
            .find(cat => cat.name === selectedCategory)
            ?.jobs.map((job, index) => (
              <motion.div
                key={job.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl shadow-lg p-6 hover:shadow-xl transition-all cursor-pointer"
              >
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
                  <job.icon className="w-6 h-6 text-purple-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {job.title}
                </h3>
                <p className="text-gray-600 mb-4">{job.description}</p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-green-600">
                      Wzrost zatrudnienia: {job.growth}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-purple-600">
                      Zarobki: {job.salary}
                    </span>
                  </div>
                </div>
              </motion.div>
            ))}
        </div>

        <div className="text-center mt-12">
          <p className="text-sm text-gray-500">
            Dane na podstawie analiz rynku pracy i prognoz rozwoju branż w Polsce
          </p>
        </div>
      </div>
    </div>
  );
}
